import { motion } from "framer-motion";
import { Landmark, ShoppingCart, Banknote } from "lucide-react";

const FORCES = [
    {
        n: "01",
        icon: Landmark,
        label: "Regulation",
        title: "Compliance stopped being optional.",
        body: "The RBI's Draft Disclosure Framework, SEBI's BRSR Core assurance mandate, and the EU CBAM's transitional reporting have converged into a single reality: climate is now a supervisory category. Banks are being asked for financed-emission attribution. Exporters are being asked for verified emissions dossiers. Both are being asked at the same time.",
        signals: [
            "RBI Draft Framework · Board-approved climate risk strategy",
            "SEBI BRSR Core · Assurance from FY 2024-25",
            "EU CBAM · Verified dossiers or default price"
        ]
    },
    {
        n: "02",
        icon: ShoppingCart,
        label: "Procurement",
        title: "Buyers are pricing you before regulators do.",
        body: "Every major European OEM, and a growing share of US and Japanese buyers, now maintain active Scope 3 supplier KPIs. Delisting for climate non-compliance has moved from ESG report footnote to procurement policy. The consequence lands on the exporter's balance sheet a full year before any regulator files a case — as a cancelled order, not a fine.",
        signals: [
            "68% of EU OEMs run supplier Scope 3 KPIs",
            "Delisting cycle: 1 quarter, not 1 year",
            "Order-book impact precedes regulatory action"
        ]
    },
    {
        n: "03",
        icon: Banknote,
        label: "Capital",
        title: "Lenders will price transition risk. Quietly at first.",
        body: "Portfolio-level financed emissions are now Basel-relevant. Banks with concentrated exposure to CBAM-exposed sectors are already modelling a 40–90 bps cost-of-capital uplift for high-intensity borrowers. That number is not in a press release. It is inside a credit committee memo — and it will be inside your term sheet before it is inside any regulation.",
        signals: [
            "40–90 bps COC uplift modelled for high-intensity portfolios",
            "PCAF-aligned financed emissions now supervisor-tracked",
            "Term sheets reprice before regulation lands"
        ]
    }
];

export default function ThreeForces() {
    return (
        <section
            className="relative py-24 lg:py-36 border-t border-navy/10"
            data-testid="section-three-forces"
        >
            <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-20">
                    <div className="lg:col-span-6">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-10 h-px bg-gold"></div>
                            <span className="font-mono text-[11px] tracking-[0.28em] uppercase text-gold">
                                The Provocation
                            </span>
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.02em] text-navy">
                            Three forces are <em className="text-gold not-italic font-light">already repricing carbon.</em> Two of them will land before regulation does.
                        </h2>
                    </div>
                    <div className="lg:col-span-5 lg:col-start-8 lg:self-end">
                        <p className="text-navy/60 text-lg leading-relaxed">
                            Carbon has stopped being a disclosure question. Regulation, procurement, and capital markets are each translating emissions into P&amp;L consequence — and they are not waiting for each other.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-navy/10 border border-navy/10">
                    {FORCES.map((f, i) => {
                        const Icon = f.icon;
                        return (
                            <motion.div
                                key={f.n}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.8, delay: i * 0.1 }}
                                className="bg-paper p-8 lg:p-10 flex flex-col gap-6 min-h-[520px]"
                                data-testid={`force-${f.n}`}
                            >
                                <div className="flex items-start justify-between">
                                    <Icon size={26} strokeWidth={1.25} className="text-gold" />
                                    <span className="chapter-num text-4xl text-gold/70">{f.n}</span>
                                </div>
                                <div className="font-mono text-[11px] tracking-[0.28em] uppercase text-navy/50">
                                    {f.label}
                                </div>
                                <h3 className="font-serif text-2xl lg:text-[28px] leading-[1.15] tracking-[-0.01em] text-navy">
                                    {f.title}
                                </h3>
                                <p className="text-navy/65 text-[15px] leading-relaxed">{f.body}</p>
                                <div className="mt-auto pt-6 border-t border-navy/10 flex flex-col gap-2">
                                    {f.signals.map((s, si) => (
                                        <div
                                            key={si}
                                            className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-navy/55 flex items-start gap-2"
                                        >
                                            <span className="text-gold mt-[3px]">·</span>
                                            <span>{s}</span>
                                        </div>
                                    ))}
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
